import React, { useEffect, useState, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  royalcaninDogProducts,
  royalcaninCatProducts,
  eukanubaProducts,
  nexgardProducts,
  simparicaProducts,
  // beenoProducts,
  bravectoProducts,
  purinaProducts,
  bobmartinProducts,
  findProductById,
  isPetProduct,
  formatZAR,
  getProductImages,
  loadProductDescription,
} from '../Components/ProductRetrieval'
import { useCart } from '../Components/Cart'
import { getWeightVariants } from '../Utils/weightVariantsUtil'
import ProductTabs from '../Components/ProductTabs/ProductTabs'

const Product = () => {
  const { productID } = useParams()
  const [selectedImage, setSelectedImage] = useState(0)
  const [quantity, setQuantity] = useState(1)

  useEffect(() => {
    setSelectedImage(0)
    setQuantity(1)
  }, [productID])

  const product = useMemo(() => findProductById(productID), [productID])

  const _isPetProduct = useMemo(
    () => (product ? isPetProduct(product.id) : false),
    [product],
  )

  const allPetProducts = useMemo(
    () => [
      ...royalcaninDogProducts.map((p) => ({ ...p, _petType: 'Dog' })),
      ...royalcaninCatProducts.map((p) => ({ ...p, _petType: 'Cat' })),
      ...eukanubaProducts.map((p) => ({ ...p, _petType: 'Dog' })),
      ...nexgardProducts.map((p) => ({ ...p, _petType: p.additionalInfo?.pet_type || 'Dog' })),
      ...simparicaProducts.map((p) => ({ ...p, _petType: p.additionalInfo?.pet_type || 'Dog' })),
      // ...beenoProducts.map((p) => ({ ...p, _petType: p.additionalInfo?.pet_type || 'Dog' })),
      ...bravectoProducts.map((p) => ({ ...p, _petType: p.additionalInfo?.pet_type || 'Dog' })),
      ...purinaProducts.map((p) => ({ ...p, _petType: p.additionalInfo?.pet_type || 'Dog' })),
      ...bobmartinProducts.map((p) => ({ ...p, _petType: p.additionalInfo?.pet_type || 'Dog' })),
    ],
    [],
  )

  const weightVariants = useMemo(() => {
    if (!product || !_isPetProduct) return []
    return getWeightVariants(product, allPetProducts)
  }, [product, _isPetProduct, allPetProducts])

  const [selectedWeightId, setSelectedWeightId] = useState(null)

  useEffect(() => {
    setSelectedWeightId(null)
  }, [productID])

  useEffect(() => {
    if (selectedWeightId === null && weightVariants.length > 0) {
      setSelectedWeightId(weightVariants[0].id)
    }
  }, [weightVariants, selectedWeightId])

  const activeVariant = useMemo(() => {
    if (!_isPetProduct || weightVariants.length <= 1) return product
    return weightVariants.find((v) => v.id === selectedWeightId) || weightVariants[0]
  }, [product, _isPetProduct, weightVariants, selectedWeightId])

  const hasWeightOptions = _isPetProduct && weightVariants.length > 1

  const displayProduct = activeVariant || product
  const displayPrice = hasWeightOptions ? activeVariant?.price : product?.price
  const isOutOfStock = displayProduct?.inStock === false || displayProduct?.inStock === 0
  const isPreorder = displayProduct?.isPreOrder === true || displayProduct?.inStock === 2

  const images = useMemo(() => getProductImages(displayProduct), [displayProduct])

  const [description, setDescription] = useState('')

  useEffect(() => {
    setDescription('')
    if (!displayProduct) return
    loadProductDescription(displayProduct).then(setDescription)
  }, [displayProduct])

  // Reset image when variant changes
  useEffect(() => {
    setSelectedImage(0)
  }, [displayProduct?.id])

  const [justAdded, setJustAdded] = useState(false)
  const { addToCart } = useCart()

  const handleAddToCart = () => {
    if (!displayProduct) return
    addToCart(displayProduct.id, displayProduct.name, quantity)
    setJustAdded(true)
    setTimeout(() => setJustAdded(false), 2000)
  }

  if (!product) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Product not found</h1>
          <Link
            to="/"
            className="mt-4 inline-block text-emerald-600 hover:text-emerald-700"
          >
            &larr; Back to Home
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
          <Link to="/" className="text-gray-400 hover:text-gray-600">Home</Link>
          <span>/</span>
          <span className="text-gray-900">{displayProduct.name}</span>
        </div>
      </div>

      {isPreorder && (
        <div className="bg-amber-50 border-y border-amber-200">
          <div className="mx-auto max-w-7xl px-4 py-3 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 text-sm font-medium text-amber-800">
              <svg className="h-4 w-4 flex-shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
              Pre-order — takes 2&ndash;3 weeks to ship
            </div>
          </div>
        </div>
      )}

      {isOutOfStock && (
        <div className="bg-gray-50 border-y border-gray-200">
          <div className="mx-auto max-w-7xl px-4 py-3 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
              <svg className="h-4 w-4 flex-shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
              This product is currently out of stock
            </div>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          {/* Left: Image gallery */}
          <div>
            <div className={`flex aspect-square items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-white ${isOutOfStock ? 'opacity-60' : ''}`}>
              {images[selectedImage] ? (
                <img
                  src={images[selectedImage].src}
                  alt={images[selectedImage].alt}
                  className={`h-full w-full object-contain p-4 ${isOutOfStock ? 'grayscale' : ''}`}
                />
              ) : (
                <span className="text-sm font-medium text-gray-400">
                  No image available
                </span>
              )}
            </div>

            {images.length > 1 && (
              <div className="mt-4 flex gap-3">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`h-20 w-20 overflow-hidden rounded-lg border-2 ${
                      idx === selectedImage
                        ? 'border-emerald-500'
                        : 'border-gray-200'
                    }`}
                  >
                    <img
                      src={img.src}
                      alt={img.label}
                      className="h-full w-full object-contain"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: Product info */}
          <div className="flex flex-col">
            <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
              {displayProduct.name}
            </h1>

            <p className={`mt-4 text-3xl font-bold sm:text-4xl ${isOutOfStock ? 'text-gray-400' : 'text-emerald-700'}`}>
              {formatZAR(displayPrice)}
            </p>

            {displayProduct.shortDescription && (
              <p className={`mt-4 text-sm leading-relaxed ${isOutOfStock ? 'text-gray-400' : 'text-gray-600'}`}>
                {displayProduct.shortDescription}
              </p>
            )}

            {displayProduct.description && !displayProduct.shortDescription && (
              <p className={`mt-4 text-sm leading-relaxed ${isOutOfStock ? 'text-gray-400' : 'text-gray-600'}`}>
                {displayProduct.description}
              </p>
            )}

            {/* Key Benefits */}
            {displayProduct.keyBenefits && displayProduct.keyBenefits.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Key Benefits</h3>
                <ul className="mt-2 space-y-1.5">
                  {displayProduct.keyBenefits.map((benefit, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Weight / Size selector */}
            {hasWeightOptions && (
              <div className="mt-6">
                <h3 className="text-sm font-semibold text-gray-700">Size</h3>
                <div className="mt-2 flex flex-wrap gap-2">
                  {weightVariants.map((v) => {
                    const isVariantOutOfStock = v.inStock === false || v.inStock === 0
                    const label = v.name.match(/\(([^)]+)\)$/)?.[1] || `${v.weight}kg`
                    return (
                      <button
                        key={v.id}
                        onClick={() => { setSelectedWeightId(v.id); setQuantity(1) }}
                        disabled={isVariantOutOfStock}
                        className={`rounded-lg border px-4 py-2 text-sm font-semibold transition ${
                          isVariantOutOfStock
                            ? 'border-gray-200 bg-gray-50 text-gray-300 cursor-not-allowed line-through'
                            : selectedWeightId === v.id
                            ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                            : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                        }`}
                      >
                        {label}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Quantity + Add to cart */}
            <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="flex items-center">
                <span className="mr-3 text-sm font-semibold text-gray-700">Qty:</span>
                <div className="flex items-center rounded-lg border border-gray-300">
                  <button
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    className="flex h-10 w-10 items-center justify-center text-lg font-medium text-gray-600 hover:bg-gray-100 transition rounded-l-lg"
                  >
                    −
                  </button>
                  <span className="flex h-10 w-12 items-center justify-center text-sm font-semibold text-gray-900 border-x border-gray-300">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity((q) => q + 1)}
                    className="flex h-10 w-10 items-center justify-center text-lg font-medium text-gray-600 hover:bg-gray-100 transition rounded-r-lg"
                  >
                    +
                  </button>
                </div>
              </div>

              {isOutOfStock ? (
                <button
                  disabled
                  className="w-full cursor-not-allowed rounded-lg bg-gray-300 px-8 py-3 text-base font-semibold text-gray-100 sm:w-auto"
                >
                  Out of stock
                </button>
              ) : (
                <button
                  onClick={handleAddToCart}
                  className={`w-full rounded-lg px-8 py-3 text-base font-semibold text-white transition sm:w-auto ${
                    justAdded
                      ? 'bg-emerald-600'
                      : 'bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800'
                  }`}
                >
                  {justAdded ? 'Added to cart' : 'Add to cart'}
                </button>
              )}
            </div>

            {/* Free shipping badge */}
            <div className="mt-6 flex items-center gap-2 text-sm text-gray-500">
              <svg className="h-5 w-5 text-emerald-500" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
              </svg>
              <span>Free shipping on orders over R500</span>
            </div>
          </div>
        </div>

        {/* Tabbed content */}
        <div className="mt-12 border-t border-gray-200 pt-10">
          <ProductTabs
            product={displayProduct}
            description={description}
            isPetProduct={_isPetProduct}
          />
        </div>
      </div>
    </div>
  )
}

export default Product
